/**
 * What to tell someone when turning on browser notifications failed
 * (TRUS-668). One sentence per kind of failure; the technical detail goes to
 * the backend log (lib/webPush reports it), not to the user.
 */
import type { WebPushFailure } from '@/lib/webPush';
import { toast } from '@/components/ui/use-toast';

type Translate = (key: string, fallback?: string) => string;

export function webPushFailureText(failure: WebPushFailure | null | undefined, t: Translate): string {
  switch (failure?.kind) {
    case 'blocked':
      return t(
        'messaging.webPush.errorBlocked',
        'Notifications are blocked for this site. Allow them in the browser settings, then try again.',
      );
    case 'unsupported':
      return t('messaging.webPush.errorUnsupported', "This browser can't receive push notifications.");
    case 'network':
      return t(
        'messaging.webPush.errorNetwork',
        "Your network is blocking notifications: this computer can't reach Google's notification service. Ask your IT team to allow firebaseinstallations.googleapis.com and fcmregistrations.googleapis.com. Until then, WeaveIn shows notifications while it is open.",
      );
    case 'service':
      return t(
        'messaging.webPush.errorService',
        "Couldn't connect to the notification service (setup issue). We've let the team know.",
      );
    default:
      return t('messaging.webPush.errorUnknown', "Couldn't turn on notifications. Try again.");
  }
}

/** The toast after a click that did not turn notifications on. */
export function toastWebPushFailure(failure: WebPushFailure | null | undefined, t: Translate): void {
  toast({
    title: t('messaging.webPush.errorTitle', 'Notifications are not on'),
    description: webPushFailureText(failure, t),
    variant: 'error',
  });
}
